import React from 'react';
import { motion } from 'framer-motion';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from './ui/accordion';

export default function FAQ() {
  const faqs = [
    {
      question: "Is Trading Compass a signal service?",
      answer: "No. Trading Compass does not give buy/sell signals. It sits between your analysis and execution, enforcing risk, structure, and discipline before you place a trade. If you're looking for signals, this is not for you."
    },
    {
      question: "I already know how to trade. How does this help me?",
      answer: "Trading Compass is built exactly for traders who know how to analyse but are still inconsistent. It focuses on: execution discipline, risk & R:R enforcement, and emotional decision control. Most traders fail not because of analysis, but because of what they do after analysis."
    },
    {
      question: "How is this different from a trading journal?",
      answer: "A journal shows you mistakes after the damage is done. Trading Compass intervenes before the trade, forcing: stop loss & take profit clarity, risk validation, and execution accountability. It's a risk manager, not a diary."
    },
    {
      question: "Does Trading Compass tell me when NOT to trade?",
      answer: "Yes — indirectly. By enforcing structure, risk, and validation rules, it blocks impulsive trades that don't meet your execution criteria. Most money is lost in trades that should never have been taken."
    },
    {
      question: "What role does AI play inside Trading Compass?",
      answer: "AI acts as a personalised trading mentor and risk manager, not an automated trader. It: questions your execution decisions, highlights behavioural patterns, identifies recurring mistakes, and helps you think like someone managing serious capital. It guides — it does not trade for you."
    },
    {
      question: "What is the 21-Day Trading Habit Challenge?",
      answer: "It's a capital-readiness filter, not a course. The challenge is designed to: expose execution & behavioural flaws, rebuild discipline through enforced structure, and create a repeatable process you can scale with capital. If you can't follow structure for 21 days, scaling capital is not the problem."
    },
    {
      question: "Why does it start with a 7-day free trial?",
      answer: "Because the first 7 days act as a mirror. They are intentionally: structured, boring, and demanding. Within 7 days, most traders clearly see: why they're inconsistent, where risk control breaks, and what needs fixing in the next 14 days. This is by design."
    },
    {
      question: "What happens after the 21-day challenge?",
      answer: "You leave with: clarity on your trading identity, a repeatable execution framework, clear strengths & weaknesses, and a roadmap to scale capital responsibly. This is not an ending — it's a foundation."
    }
  ];

  return (
    <section className="py-10 md:py-16 bg-slate-50">
      <div className="mx-auto max-w-[1200px] px-4 sm:px-6 lg:px-8">
        <motion.h2 
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.4 }}
          className="text-3xl md:text-4xl lg:text-5xl font-semibold text-[#337AF7] tracking-tight text-center mb-12"
          data-testid="faq-title"
        >
          Frequently Asked Questions
        </motion.h2>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="max-w-3xl mx-auto"
        >
          <Accordion type="single" collapsible className="space-y-4" data-testid="faq-accordion">
            {faqs.map((faq, index) => (
              <AccordionItem 
                key={index} 
                value={`item-${index}`}
                className="bg-white rounded-2xl border border-slate-200 shadow-sm px-6 py-2 hover:shadow-md hover:border-slate-300 transition-all duration-200"
                data-testid={`faq-accordion-item-${index + 1}`}
              >
                <AccordionTrigger className="text-left text-base md:text-lg text-[#162959] hover:text-[#337AF7] transition-colors">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-sm md:text-base text-[#162959]/70 pt-2 pb-4">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>
      </div>
    </section>
  );
}